import { DownOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { thematicColors } from '@revenuemarket/design';
import { ArrowCircle, Splitted } from '@revenuemarket/ui';
import { FormattedInput } from '@revenuemarket/ui/FormattedInput';
import { Dropdown, Form, Typography } from 'antd';

import CategorySimpleLabelSelctor from '~components/Labeling/LabelManagement/CategorySimpleLabelSelctor';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategoriesTree } from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { CashFlowType } from '~models/cashFlow';

import BusinessEntityLabelSelect from './BusinessEntityLabelSelect';
import GroupLabelSelect from './GroupLabelSelect';
import useTransactionMappingStore, { SplittedTransaction } from './TransactionMappingStore';

interface Props {
  idx: number;
  item: SplittedTransaction;
  currencyCode?: string;
}

const SplittedItem: React.FC<Props> = ({ idx, item, currencyCode }) => {
  const { setSplittedTransaction } = useTransactionMappingStore();
  const { companyId } = useDemoAwareCompanyId();
  const { data: treeData } = useCategoriesTree({ companyId });
  const categoryTrees = treeData?.Data?.data?.tree?.children;
  const flattenedTree = flattenTree(categoryTrees ?? [])
    .filter((item) => !!item.value?.name)
    .filter((treeItem) => treeItem.value?.cashFlowType === item.cashFlowType);

  const isKrw = currencyCode === 'KRW';

  const targetCategory = item.categoryLabelKey
    ? flattenedTree.find((treeItem) => treeItem.value?.categoryLabelKey === item.categoryLabelKey)
    : undefined;

  return (
    <Flex
      direction="column"
      gap={8}
      style={{
        width: '100%',
      }}
    >
      <Flex gap={4} align="center">
        <Splitted />
        {item.cashFlowType === CashFlowType.IN ? (
          <ArrowCircle fill={'#4DC987'} />
        ) : (
          <ArrowCircle
            fill={'#FF7A70'}
            style={{
              transform: 'rotate(180deg)',
            }}
          />
        )}
        <Typography.Text
          style={{
            fontSize: 14,
          }}
        >
          직접 분할
        </Typography.Text>
      </Flex>
      <Flex gap={8} align="center">
        <BusinessEntityLabelSelect
          style={{ width: '50%' }}
          value={item.businessEntityId}
          onChange={(value) => {
            setSplittedTransaction({ businessEntityId: value }, idx);
          }}
        />

        <Flex style={{ flex: 1 }} gap={4} align="center">
          <Dropdown
            menu={{
              items: [
                { label: '입금', key: CashFlowType.IN },
                {
                  label: '출금',
                  key: CashFlowType.OUT,
                },
              ],
              onClick: (item) => {
                setSplittedTransaction({ cashFlowType: item.key as unknown as CashFlowType }, idx);
              },
            }}
            trigger={['click']}
          >
            <a onClick={(e) => e.preventDefault()}>
              <Flex
                gap={4}
                align="center"
                style={{
                  width: 43,
                }}
              >
                <Typography.Text
                  style={{
                    color: thematicColors.primary.medium,
                  }}
                >
                  {item.cashFlowType === CashFlowType.IN ? '입금' : '출금'}
                </Typography.Text>
                <DownOutlined style={{ color: thematicColors.primary.medium }} />
              </Flex>
            </a>
          </Dropdown>
          <FormattedInput
            placeholder="분할 금액 입력"
            suffix={isKrw ? '원' : ''}
            prefix={isKrw ? '' : currencyCode}
            defaultValue={item.amount + ''}
            onChange={(value) => {
              setSplittedTransaction({ amount: Number(value) }, idx);
            }}
            formatter={(value: string) => {
              if (!value) return '';
              if (value.endsWith('.')) return value; // 소수점만 입력된 경우 그대로 반환
              if (isNaN(parseFloat(value))) return '';
              return parseFloat(value).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              });
            }}
            deformatter={(value: string) => {
              if (!value) return '';
              return value.replace(/,/g, '');
            }}
          />
        </Flex>
      </Flex>
      <Flex gap={8} align="flex-start">
        <Form.Item
          style={{ flex: 1 }}
          validateStatus={!item.categoryLabelKey ? 'error' : undefined}
          help={!item.categoryLabelKey ? '계정 분류는 필수값입니다.' : undefined}
        >
          <CategorySimpleLabelSelctor
            treeList={flattenedTree}
            onSelect={(categoryLabelKey) => {
              setSplittedTransaction({ categoryLabelKey }, idx);
            }}
            targetCategory={targetCategory}
          />
        </Form.Item>
        <GroupLabelSelect
          style={{ flex: 1 }}
          value={item.groupLabelId}
          onChange={(value) => {
            setSplittedTransaction({ groupLabelId: value }, idx);
          }}
        />
      </Flex>
    </Flex>
  );
};

export default SplittedItem;
